// Shared fetch wrapper for /api/* endpoints (M-C2.0+ Phase 3+ wiring).
// Adds credentials:'include' for HttpOnly cookies, parses JSON envelopes,
// and exposes a typed error class with the structured error code from API_CATALOG.md.

export interface ApiOk<T> { ok: true; data: T; }
export interface ApiErr { ok: false; error: { code: string; message: string }; }
export type ApiResponse<T> = ApiOk<T> | ApiErr;

export class ApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

/**
 * fetch wrapper that:
 *   - Always sends credentials (cookies)
 *   - Sets Content-Type: application/json unless caller overrides
 *   - Throws ApiError on non-2xx with the structured error.code
 *   - Returns the .data payload on 2xx (or full response if no .data)
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  // Resolve relative paths against window.location.origin (jsdom-safe, SSR-safe).
  // fetch() in the browser handles relative URLs natively, but jsdom/Node test envs
  // need an explicit base. Absolute URLs (http(s)://...) are passed through unchanged.
  let url: string;
  try {
    url = new URL(path, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').toString();
  } catch {
    url = path;
  }
  const res = await fetch(url, {
    ...init,
    credentials: 'include',
    headers,
  });
  let json: unknown = null;
  try { json = await res.json(); } catch { /* may be empty */ }
  if (!res.ok || (json && typeof json==='object' && json!==null && (json as {ok?:unknown}).ok===false)) {
    function isErr(x:unknown):x is {error:{code:string;message:string}}{return typeof x==='object'&&x!==null&&typeof (x as {error?:unknown}).error==='object'&&(x as {error?:unknown}).error!==null;}const code = isErr(json)?json.error.code:'http_error';
    const message = isErr(json)?json.error.message: `Request failed (${res.status})`;
    throw new ApiError(code, message, res.status);
  }
  if (json && typeof json==='object' && json!==null && (json as {ok?:unknown}).ok===true && 'data' in (json as Record<string,unknown>)) return (json as {data:T}).data;
  return (json ?? null) as T;
}
